import { Button, Card, CardHeader, Icon } from "@helmr/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { fetchImportMappings, saveImportMapping, type ImportMapping } from "../../../api/expenses";
import { useServices } from "../../../app/services";
import { PageHead, TableSkeleton, css as s, useScope } from "../../payroll/kit";
import m from "./import.module.css";
import { MappingStep } from "./MappingStep";
import { readStatement, StatementReadError } from "./read";
import { Review } from "./Review";
import { applyMapping, parseStatement, type BadRow, type ColumnMapping, type Grid, type Txn } from "./statement";

type Stage =
  | { kind: "pick"; error?: string }
  | { kind: "reading"; name: string }
  | { kind: "mapping"; name: string; grid: Grid; headerRow: number; signature: string; guess: Partial<ColumnMapping>; reason: string }
  | { kind: "review"; name: string; txns: Txn[]; bad: BadRow[] };

/**
 * `/expenses/import`: pick a bank statement, check the columns (once per layout), choose transactions, enter them.
 * The file is read and parsed on this device and is never uploaded: only the rows the user picks reach the API.
 */
export function ImportPage() {
  const sc = useScope();
  const { client } = useServices();
  const qc = useQueryClient();
  const [stage, setStage] = useState<Stage>({ kind: "pick" });
  const run = useRef(0);

  const openFile = async (file: File) => {
    const mine = ++run.current;
    setStage({ kind: "reading", name: file.name });
    try {
      const grid = await readStatement(file);
      // Saved layouts are a convenience: if they cannot be read (older server, no access), the columns are detected or asked for.
      const saved = await qc.fetchQuery({ queryKey: ["expenses", "import-mappings", ...sc.scopeKey], queryFn: () => fetchImportMappings(client), staleTime: 60_000 }).then((r) => r.mappings).catch(() => ({}));
      if (mine !== run.current) return;
      const out = parseStatement(grid, { digits: sc.digits, saved });
      if (out.kind === "empty") return setStage({ kind: "pick", error: "This file has no rows to read." });
      if (out.kind === "needs-mapping") return setStage({ kind: "mapping", name: file.name, grid, headerRow: out.headerRow, signature: out.signature, guess: out.guess, reason: out.reason });
      if (out.txns.length === 0 && out.bad.length === 0) return setStage({ kind: "pick", error: "No transactions were found in this file." });
      setStage({ kind: "review", name: file.name, txns: out.txns, bad: out.bad });
    } catch (e) {
      if (mine === run.current) setStage({ kind: "pick", error: e instanceof StatementReadError ? e.message : "This file could not be read. Try the CSV or Excel version." });
    }
  };

  const mapped = async (mapping: ColumnMapping, signature: string, grid: Grid, name: string) => {
    const read = applyMapping(grid, mapping, sc.digits);
    if (read.txns.length === 0) return false;
    // Remember the layout for next month. A failure to save must not stop this import.
    void saveImportMapping(client, signature, mapping as ImportMapping).then(() => qc.invalidateQueries({ queryKey: ["expenses", "import-mappings"] })).catch(() => undefined);
    setStage({ kind: "review", name, txns: read.txns, bad: read.bad });
    return true;
  };

  return (
    <div className={`${s.page} ${m.pad}`}>
      <PageHead title="Import bank statement" sub="Pick the transactions to enter as expenses.">
        <Button as={Link} to="/expenses" size="sm">Back</Button>
      </PageHead>
      {stage.kind === "pick" ? <Pick error={stage.error} onFile={(f) => void openFile(f)} /> : null}
      {stage.kind === "reading" ? <Card aria-label="Reading statement"><CardHeader title={stage.name} subtitle="Reading the statement on this device…" /><TableSkeleton label="Reading statement" /></Card> : null}
      {stage.kind === "mapping" ? (
        <MappingStep name={stage.name} grid={stage.grid} headerRow={stage.headerRow} signature={stage.signature} guess={stage.guess} reason={stage.reason} digits={sc.digits} onCancel={() => setStage({ kind: "pick" })} onDone={(mp) => mapped(mp, stage.signature, stage.grid, stage.name)} />
      ) : null}
      {stage.kind === "review" ? <Review key={stage.name} name={stage.name} txns={stage.txns} bad={stage.bad} sc={sc} onChooseAnother={() => setStage({ kind: "pick" })} /> : null}
    </div>
  );
}

function Pick({ error, onFile }: { error?: string; onFile: (f: File) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [sampleErr, setSampleErr] = useState<string | null>(null);
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };
  // `vite --mode mock` only: the sample statements are served by the mock API (src/test/expense-import-preview.ts), so a production
  // build carries neither the files nor the code that loads them.
  const sample = async (which: "hdfc" | "sbi") => {
    if (import.meta.env.MODE !== "mock") return;
    setSampleErr(null);
    try {
      const file = which === "hdfc" ? "hdfc-sample.csv" : "sbi-sample.xlsx";
      const res = await fetch(`${import.meta.env.VITE_API_BASE ?? ""}/__mock/samples/${file}`);
      if (!res.ok) throw new Error("sample");
      onFile(new File([await res.blob()], file));
    } catch {
      setSampleErr("Could not load the sample.");
    }
  };
  return (
    <Card aria-label="Choose a statement">
      <CardHeader title="Choose a statement" subtitle="CSV or Excel (.xlsx, .xls) from HDFC, ICICI, SBI, Axis, Kotak and most other banks." />
      <div className={m.drop} data-over={over} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
        <Icon name="file" size={28} />
        <h2>Drop a statement here</h2>
        <p>Download it from net banking as Excel or CSV. PDF statements are not supported yet.</p>
        <input ref={input} aria-label="Statement file" className={m.fileInput} type="file" tabIndex={-1} accept=".csv,.txt,.tsv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
        <Button variant="primary" onClick={() => input.current?.click()}>Choose file</Button>
        <p className={m.privacy}>The file is read on this device. It is not uploaded; only the transactions you choose are saved.</p>
        {import.meta.env.MODE === "mock" ? (
          <div className={m.samples}>
            <Button size="sm" onClick={() => void sample("hdfc")}>Try the sample HDFC CSV</Button>
            <Button size="sm" onClick={() => void sample("sbi")}>Try the sample SBI Excel</Button>
          </div>
        ) : null}
      </div>
      {error || sampleErr ? <p className={s.formErr} role="alert" style={{ margin: "0 16px 16px" }}>{error ?? sampleErr}</p> : null}
    </Card>
  );
}

